import { Application } from '../../_models/feature';

/**
 * Kuba-branded translation keys that a white-label platform must not show.
 *
 * The i18n files are shared by every application, so a key such as ADMIN_NEWS
 * ("Nyheter fra Kuba") cannot simply be reworded — it would change IK, BA and
 * the rest as well. Instead each white-label application maps the offending
 * key onto its own key, the same way Medi3 does with ADMIN_NEWS_MEDI3.
 *
 * Applies wherever a Kuba-branded key reaches the UI, including the feature
 * tree, which translates whatever translationKey the Application.Features JSON
 * happens to carry.
 */
const BRANDING_KEY_OVERRIDES: {
  [applicationId: number]: { [translationKey: string]: string };
} = {
  [Application.AGIL]: {
    ADMIN_NEWS: 'ADMIN_NEWS_AGIL'
  },
  [Application.SALUTIS]: {
    ADMIN_NEWS: 'ADMIN_NEWS_SALUTIS'
  }
};

/**
 * Overrides that hold for every application, not just the white-label ones.
 *
 * "Om Kuba" is the odd one out: the page behind it describes the system the
 * customer is actually logged into, so it reads as the wrong product name on
 * IK, BA, Agil and Salutis alike. All of them show the brand-neutral
 * "Om systemet" instead.
 *
 * The admin "Customize Homepage" menu item (feature node 31) is now the editor
 * for exactly that text and nothing else, so it takes the same label. Medi3
 * already carried its own SYSTEM_INFORMATION spelling of the same node.
 */
const GLOBAL_KEY_OVERRIDES: { [translationKey: string]: string } = {
  ABOUT_KUBA: 'ABOUT_SYSTEM',
  // Some feature trees carry this malformed variant of the key.
  'ABOUT_KUBA IK': 'ABOUT_SYSTEM',
  CUSTOMIZE_HOMEPAGE: 'ABOUT_SYSTEM',
  SYSTEM_INFORMATION: 'ABOUT_SYSTEM'
};

/**
 * Returns the translation key to render for the given application, replacing
 * Kuba-branded keys on the platforms that have an override. Unknown keys and
 * applications without overrides are returned untouched.
 * @param translationKey the key as it comes from the code or the feature JSON
 * @param applicationId  BaseServices.ApplicationId of the current session
 */
export function brandedTranslationKey(
  translationKey: string,
  applicationId: number
): string {
  if (!translationKey) {
    return translationKey;
  }
  const overrides = BRANDING_KEY_OVERRIDES[+applicationId];
  return (
    (overrides && overrides[translationKey]) ||
    GLOBAL_KEY_OVERRIDES[translationKey] ||
    translationKey
  );
}
